import type {
  ReploAgent,
  SandboxFile,
} from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import * as React from "react";

import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { routes } from "@v2/utils/router";
import { generatePath, Link } from "react-router-dom";

import { DevicePreview } from "./DevicePreview";
import SandboxPageCardMenu from "./SandboxPageCardMenu";

const CARD_CONSTANTS = {
  PREVIEW_HEIGHT: 155,
  MOBILE_MAX_WIDTH: 140,
  MIN_CARD_WIDTH: 300,
} as const;

interface SandboxPageCardProps {
  file: SandboxFile;
  agent: ReploAgent;
  previewBaseUrl: string | null;
  layoutClassName?: string;
}

const SandboxPageCard: React.FC<SandboxPageCardProps> = ({
  file,
  agent,
  previewBaseUrl,
  layoutClassName,
}) => {
  const { project } = useCurrentProjectContext();
  const isMobile = useIsMobile();

  const [previewLoadState, setPreviewLoadState] = React.useState<{
    url: string | undefined;
    loaded: boolean;
  }>({ url: undefined, loaded: false });

  if (!project) {
    return null;
  }

  const previewUrl = previewBaseUrl
    ? `${previewBaseUrl}${file.route === "/" ? "" : file.route}`
    : undefined;

  const isPreviewLoaded =
    previewLoadState.loaded && previewLoadState.url === previewUrl;

  return (
    <Link
      to={`${generatePath(routes.agent.chat, { projectId: project.id })}?pageRoute=${encodeURIComponent(file.route)}`}
      className="h-full group cursor-pointer"
    >
      <div
        className={twMerge(
          "border-0.5 border-border rounded-lg bg-sheet group-hover:bg-menu-item-hover flex flex-col cursor-pointer shadow-sm transition-all duration-200 h-full group-hover:scale-[0.99] group-hover:shadow-lg",
          layoutClassName,
        )}
        style={{
          minWidth: isMobile
            ? CARD_CONSTANTS.MOBILE_MAX_WIDTH
            : CARD_CONSTANTS.MIN_CARD_WIDTH,
        }}
      >
        <div className="p-4 pb-0 flex flex-col gap-2">
          <div
            className="relative flex justify-center items-start gap-4 overflow-hidden"
            style={{ height: `${CARD_CONSTANTS.PREVIEW_HEIGHT}px` }}
          >
            {!isPreviewLoaded && (
              <div className="absolute inset-0 rounded-md bg-surface animate-pulse z-10" />
            )}
            {previewUrl && (
              <DevicePreview
                pageRoute={file.route}
                className="w-full h-full pointer-events-none"
                isScrollable={false}
                hideScrollbars={true}
                url={previewUrl}
                iframeSrc={previewUrl}
                mobileClassName="pointer-events-none h-full w-full"
                maxMobileWidth={CARD_CONSTANTS.MOBILE_MAX_WIDTH}
                desktopClassName="pointer-events-none h-full w-full border-l-[1px] border-r-[1px] border-t-[1px] border-border/25 rounded-t-md"
                cropToFit
                onIframeLoad={() => {
                  return setPreviewLoadState({ url: previewUrl, loaded: true });
                }}
              />
            )}
          </div>
        </div>

        <div className="px-4 py-3 flex flex-col gap-2 border-t-0.5 border-border bg-white rounded-b-lg">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div
                className="header-h6 text-default truncate min-w-0"
                title={file.route}
              >
                {file.route}
              </div>

              <span className="text-xs text-muted bg-surface px-1.5 py-0.5 rounded">
                Draft
              </span>
            </div>

            <div className="shrink-0">
              <SandboxPageCardMenu file={file} agent={agent} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SandboxPageCard;
